import React, { useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TrashIcon,
  ScaleIcon,
  TagIcon,
  MinusIcon,
} from '@heroicons/react/20/solid';
import { turkishToLower } from '../../utils/stringUtils';
import { kanunMaddeAgaciOlustur } from '../../utils/kanunMaddeAgaciOlustur';
import { kanunTamAdi } from '../../utils/kanunMaddeParse';

const DIGER_KEY = '__diger__';

// Token: "<seviye>:<anahtar>" — her seviye TEK token üretir (cascading YOK).
const tokenYap = (level, key) => `${level}:${key}`;
const kanonikKisa = (canonical) => {
  const i = canonical.indexOf(' m. ');
  return i === -1 ? canonical : canonical.slice(i + 4);
};
const tokenEtiketi = (token) => {
  const idx = token.indexOf(':');
  if (idx === -1) return token;
  const level = token.slice(0, idx);
  const key = token.slice(idx + 1);
  if (level === 'kanun') return `${key} · tüm maddeler`;
  if (level === 'madde') return `${key} · tüm varyantlar`;
  return key;
};

const KanunMaddeAgaci = ({ kanunMaddeleri, secilenKanunMaddeleri, setSecilenKanunMaddeleri }) => {
  const [aramaMetni, setAramaMetni] = useState('');
  const [acikKanunlar, setAcikKanunlar] = useState({}); // varsayılan AÇIK (yalnız false kapalı)
  const [acikMaddeler, setAcikMaddeler] = useState({}); // varsayılan KAPALI (yalnız true açık)

  const agac = useMemo(() => kanunMaddeAgaciOlustur(kanunMaddeleri), [kanunMaddeleri]);

  const aramaVar = !!aramaMetni.trim();
  const filtreliAgac = useMemo(() => {
    const q = turkishToLower(aramaMetni.trim());
    if (!q) return agac;
    const kanunlar = agac.kanunlar
      .map((k) => {
        const kanunUyar =
          turkishToLower(k.kanunKey).includes(q) || turkishToLower(kanunTamAdi(k.kanunKey)).includes(q);
        if (kanunUyar) return k;
        const maddeler = k.maddeler
          .map((m) => {
            if (turkishToLower(m.maddeKey).includes(q)) return m;
            const varyantlar = m.varyantlar.filter(
              (v) => turkishToLower(v.canonical).includes(q) || turkishToLower(v.raw).includes(q)
            );
            return varyantlar.length ? { ...m, varyantlar, count: varyantlar.length } : null;
          })
          .filter(Boolean);
        return maddeler.length ? { ...k, maddeler } : null;
      })
      .filter(Boolean);
    const digerRaws = agac.diger.raws.filter((r) => turkishToLower(r).includes(q));
    return { kanunlar, diger: { count: digerRaws.length, raws: digerRaws } };
  }, [agac, aramaMetni]);

  const secimSet = useMemo(() => new Set(secilenKanunMaddeleri), [secilenKanunMaddeleri]);

  const toggleToken = (token) =>
    setSecilenKanunMaddeleri((prev) =>
      prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]
    );

  const kanunAcikMi = (key) => aramaVar || acikKanunlar[key] !== false;
  const maddeAcikMi = (key) => aramaVar || acikMaddeler[key] === true;
  const toggleKanun = (key) =>
    setAcikKanunlar((p) => ({ ...p, [key]: p[key] === false ? true : false }));
  const toggleMadde = (key) => setAcikMaddeler((p) => ({ ...p, [key]: !p[key] }));

  const ikonBoyutu = 'h-4 w-4';

  const Sayac = ({ n }) =>
    n > 0 ? <span className="app-num ml-2 shrink-0 text-[11px] text-slate-400">{n}</span> : null;

  const renderVaryant = (v) => {
    const token = tokenYap('varyant', v.canonical);
    const secili = secimSet.has(token);
    return (
      <div
        key={v.canonical}
        className="flex items-center rounded-lg py-1.5 pl-9 pr-2 transition-colors hover:bg-slate-100"
      >
        <input
          type="checkbox"
          checked={secili}
          onChange={() => toggleToken(token)}
          aria-label={`${v.canonical} filtresini değiştir`}
          className={`form-checkbox ${ikonBoyutu} mr-2 rounded text-amber-500 focus:ring-amber-400`}
        />
        <button
          type="button"
          onClick={() => toggleToken(token)}
          className={`flex min-w-0 flex-1 items-center text-left ${
            secili ? 'font-medium text-amber-700' : 'text-slate-600'
          }`}
          title={v.canonical}
        >
          <TagIcon className={`${ikonBoyutu} mr-2 shrink-0 ${secili ? 'text-amber-500' : 'text-slate-300'}`} />
          <span className="app-num min-w-0 flex-1 truncate text-sm">{kanonikKisa(v.canonical)}</span>
        </button>
      </div>
    );
  };

  const renderMadde = (m) => {
    const token = tokenYap('madde', m.maddeKey);
    const secili = secimSet.has(token);
    const altVaryantlar = m.varyantlar.filter((v) => v.canonical !== m.maddeKey);
    const acilabilir = altVaryantlar.length > 0;
    const acik = maddeAcikMi(m.maddeKey);

    return (
      <div key={m.maddeKey}>
        <div className="flex items-center rounded-lg py-1.5 pl-5 pr-2 transition-colors hover:bg-slate-100">
          <input
            type="checkbox"
            checked={secili}
            onChange={() => toggleToken(token)}
            aria-label={`${m.maddeKey} filtresini değiştir`}
            className={`form-checkbox ${ikonBoyutu} mr-2 rounded text-amber-500 focus:ring-amber-400`}
          />
          <button
            type="button"
            onClick={() => (acilabilir ? toggleMadde(m.maddeKey) : toggleToken(token))}
            className={`flex min-w-0 flex-1 items-center text-left ${
              secili ? 'font-medium text-amber-700' : 'text-slate-700'
            }`}
            title={acilabilir ? `${m.maddeKey} (tüm varyantlar)` : m.maddeKey}
          >
            {acilabilir ? (
              acik ? (
                <ChevronDownIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
              ) : (
                <ChevronRightIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
              )
            ) : (
              <MinusIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-300`} />
            )}
            <span className="app-num min-w-0 flex-1 truncate text-sm">m. {m.maddeNo}</span>
            {acilabilir && <Sayac n={altVaryantlar.length} />}
          </button>
        </div>
        {acilabilir && acik && <div>{altVaryantlar.map(renderVaryant)}</div>}
      </div>
    );
  };

  const renderKanun = (k) => {
    const token = tokenYap('kanun', k.kanunKey);
    const secili = secimSet.has(token);
    const acik = kanunAcikMi(k.kanunKey);
    return (
      <div key={k.kanunKey} className="mb-1">
        <div className="flex items-center rounded-lg p-2 transition-colors hover:bg-slate-100">
          <input
            type="checkbox"
            checked={secili}
            onChange={() => toggleToken(token)}
            aria-label={`${k.kanunKey} kanununun tüm maddelerini filtrele`}
            className={`form-checkbox ${ikonBoyutu} mr-2 rounded text-amber-500 focus:ring-amber-400`}
          />
          <button
            type="button"
            onClick={() => toggleKanun(k.kanunKey)}
            className={`flex min-w-0 flex-1 items-center text-left ${
              secili ? 'font-semibold text-amber-700' : 'font-semibold text-slate-800'
            }`}
            title={kanunTamAdi(k.kanunKey)}
          >
            {acik ? (
              <ChevronDownIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
            ) : (
              <ChevronRightIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
            )}
            <ScaleIcon className={`${ikonBoyutu} mr-2 shrink-0 ${secili ? 'text-amber-500' : 'text-amber-400'}`} />
            <span className="min-w-0 flex-1 truncate text-sm">{k.kanunKey}</span>
            <Sayac n={k.count} />
          </button>
        </div>
        {acik && <div>{k.maddeler.map(renderMadde)}</div>}
      </div>
    );
  };

  const renderDiger = () => {
    const d = filtreliAgac.diger;
    if (!d || d.count === 0) return null;
    const acik = maddeAcikMi(DIGER_KEY);
    return (
      <div className="mb-1">
        <div className="flex items-center rounded-lg p-2 transition-colors hover:bg-slate-100">
          <button
            type="button"
            onClick={() => toggleMadde(DIGER_KEY)}
            className="flex min-w-0 flex-1 items-center text-left font-semibold text-slate-500"
            title="Sınıflandırılamayan kanun maddeleri"
          >
            {acik ? (
              <ChevronDownIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
            ) : (
              <ChevronRightIcon className={`${ikonBoyutu} mr-1 shrink-0 text-slate-400`} />
            )}
            <span className="min-w-0 flex-1 truncate text-sm italic">Diğer / Sınıflandırılamayan</span>
            <Sayac n={d.count} />
          </button>
        </div>
        {acik && (
          <div>
            {d.raws.map((raw) => {
              const token = tokenYap('varyant', raw);
              const secili = secimSet.has(token);
              return (
                <div
                  key={raw}
                  className="flex items-center rounded-lg py-1.5 pl-9 pr-2 transition-colors hover:bg-slate-100"
                >
                  <input
                    type="checkbox"
                    checked={secili}
                    onChange={() => toggleToken(token)}
                    aria-label={`${raw} filtresini değiştir`}
                    className={`form-checkbox ${ikonBoyutu} mr-2 rounded text-amber-500 focus:ring-amber-400`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleToken(token)}
                    className={`flex min-w-0 flex-1 items-center text-left ${
                      secili ? 'font-medium text-amber-700' : 'text-slate-500'
                    }`}
                    title={raw}
                  >
                    <TagIcon className={`${ikonBoyutu} mr-2 shrink-0 ${secili ? 'text-amber-500' : 'text-slate-300'}`} />
                    <span className="min-w-0 flex-1 truncate text-sm">{raw}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const bosMu = filtreliAgac.kanunlar.length === 0 && filtreliAgac.diger.count === 0;

  return (
    <div className="space-y-4">
      {secilenKanunMaddeleri.length > 0 && (
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-grow flex-wrap gap-1.5">
            {secilenKanunMaddeleri.map((token) => (
              <span key={token} className="app-chip app-chip-selected">
                {tokenEtiketi(token)}
                <button
                  type="button"
                  onClick={() => toggleToken(token)}
                  className="ml-0.5 text-amber-700 hover:text-amber-900"
                  aria-label={`${tokenEtiketi(token)} filtresini kaldır`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSecilenKanunMaddeleri([])}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700"
          >
            <TrashIcon className="h-4 w-4" />
            Temizle
          </button>
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          placeholder="Kanun / madde ara..."
          value={aramaMetni}
          onChange={(e) => setAramaMetni(e.target.value)}
          className="app-input py-2 pl-9 text-sm"
          aria-label="Kanun maddelerinde ara"
        />
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>

      <div>
        {bosMu ? (
          <p className="px-3 py-2 text-sm text-slate-400">Hiç sonuç bulunamadı.</p>
        ) : (
          <>
            {filtreliAgac.kanunlar.map(renderKanun)}
            {renderDiger()}
          </>
        )}
      </div>
    </div>
  );
};

export default KanunMaddeAgaci;
